import type { ImpiantiPaginationDTO } from '@/models/dto/impianto/impianti-pagination-dto'
import type { ImpiantoDTO } from '@/models/dto/impianto/impianto-dto'
import type { ReportImpiantoDTO } from '@/models/dto/report/report-impianto-dto'
import type { ReportImpiantoPaginationDTO } from '@/models/dto/report/report-impianto-pagination-dto'
import { mapToFacilities } from '@/models/mappers/facility/facilities-mapper'
import { mapToFacility, mapToImpiantoDTO, mapToNuovoImpiantoDTO } from '@/models/mappers/facility/facility-mapper'
import { mapToFacilityReport } from '@/models/mappers/report/facility-report-mapper'
import { mapToFacilityReports } from '@/models/mappers/report/facility-reports-mapper'
import type { FacilitiesWithPagination } from '@/models/types/facility/facilities-with-pagination'
import type { Facility, NewFacility } from '@/models/types/facility/facility'
import type { FacilityReport } from '@/models/types/report/facility-report'
import type { FacilityReportsWithPagination } from '@/models/types/report/facility-reports-with-pagination'
import type { ReportType } from '@/models/types/report/report-type'
import type { OrderType } from '@/types/table'
import { capitalizeFirstLetter } from '@/utils/utils'
import { makeRequest, type MakeRequestOptions } from './make-request'
import logger from '@/lib/logger'

type FacilityParams = {
    order?: OrderType
    sort?: keyof ImpiantoDTO
    search?: string
    page?: string
} & Partial<Record<keyof Facility, string>>

type FacilityReportsParams = {
    order?: OrderType
    sort?: keyof ReportImpiantoDTO
    search?: string
    page?: string
}

type FacilityCrudType = () => {
    getOne: (id: number, options?: MakeRequestOptions) => Promise<Facility>
    getAll: (options?: MakeRequestOptions) => Promise<Facility[]>
    getMany: (
        itemsPerPage?: string,
        params?: FacilityParams,
        onlyEnabled?: boolean,
        options?: MakeRequestOptions,
    ) => Promise<FacilitiesWithPagination>
    post: (facility: NewFacility, options?: MakeRequestOptions) => Promise<Facility>
    put: (facility: Facility, options?: MakeRequestOptions) => Promise<Facility>
    delete: (id: number, options?: MakeRequestOptions) => Promise<void>
    getManyReports: (
        itemsPerPage?: string,
        params?: FacilityReportsParams,
        options?: MakeRequestOptions,
    ) => Promise<FacilityReportsWithPagination>
    generateReport: (
        type: ReportType,
        facilityId: number,
        datetimeFrom: string,
        datetimeTo: string,
        options?: MakeRequestOptions,
    ) => Promise<FacilityReport>
    deleteReport: (id: number, options?: MakeRequestOptions) => Promise<void>,
    putIfcMeta: (id: number, ifcmeta: any, options?: MakeRequestOptions) => Promise<void>
}

const FACILITY_URL =
    typeof window === 'undefined'
        ? `${process.env.API_URL}/api/v1/Impianti`
        : `${location.origin}${process.env.NEXT_PUBLIC_BASEPATH}/api/v1/Impianti`
const FACILITY_TAG = 'facility'

const facilityCRUD: FacilityCrudType = () => ({
    getOne: async (id, options) => {
        const url = new URL(`${FACILITY_URL}/${id.toString()}`)
        const impiantiDTO = await makeRequest<ImpiantoDTO>(url, {
            next: { tags: [FACILITY_TAG] },
            method: 'GET',
            ...options,
        })
        const facility = mapToFacility(impiantiDTO)
        return facility
    },
    getAll: async (options) => {
        const url = new URL(FACILITY_URL)
        const impiantiDTOs = await makeRequest<ImpiantoDTO[]>(url, {
            next: { tags: [FACILITY_TAG] },
            method: 'GET',
            ...options,
        })
        const facilities = impiantiDTOs.map((impiantoDTO) => mapToFacility(impiantoDTO))
        return facilities
    },
    getMany: async (itemsPerPage, params, onlyEnabled, _options) => {
        const url = new URL(`${FACILITY_URL}/Pagination`)
        url.searchParams.append('PageSize', itemsPerPage ?? '999999')
        if (params?.page) url.searchParams.append('PageIndex', params.page)
        if (params?.order) url.searchParams.append('Descending', params.order === 'desc' ? 'true' : 'false')
        if (params?.sort) url.searchParams.append('SortBy', capitalizeFirstLetter(params.sort))

        if (onlyEnabled) url.searchParams.append('OnlyActive', 'true')

        if (params?.search) url.searchParams.append('Filters[search]', params.search)

        if (params?.name) url.searchParams.append('Filters[Nome]', params.name)
        if (params?.address) url.searchParams.append('Filters[Indirizzo]', params.address)
        if (params?.city) url.searchParams.append('Filters[Città]', params.city)
        if (params?.province) url.searchParams.append('Filters[Provincia]', params.province)
        if (params?.postalCode) url.searchParams.append('Filters[Cap]', params.postalCode)
        if (params?.enabled) url.searchParams.append('Filter[Attivo]', params.enabled)
        if (params?.latitude) url.searchParams.append('Filters[Latitudine]', params.latitude)
        if (params?.longitude) url.searchParams.append('Filters[Longitudine]', params.longitude)
        if (params?.description) url.searchParams.append('Filters[Descrizione]', params.description)
        if (params?.status) url.searchParams.append('Filters[StatoImpianto]', params.status)

        const impiantiPaginationDTO = await makeRequest<ImpiantiPaginationDTO>(url, {
            method: 'GET',
            next: { tags: [FACILITY_TAG] },
            ...params,
        })
        const impiantiPagination = mapToFacilities(impiantiPaginationDTO)
        return impiantiPagination
    },
    post: async (facility, options) => {
        const newImpiantiDTO = mapToNuovoImpiantoDTO(facility)
        const url = new URL(FACILITY_URL)
        const impiantiDTOResponse = await makeRequest<ImpiantoDTO>(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            next: { tags: [FACILITY_TAG] },
            body: JSON.stringify(newImpiantiDTO),
            ...options,
        })
        const newFacility = mapToFacility(impiantiDTOResponse)
        return newFacility
    },
    put: async (facility, options) => {
        const impiantiDTO = mapToImpiantoDTO(facility)
        const url = new URL(FACILITY_URL)
        const impiantiDTOResponse = await makeRequest<ImpiantoDTO>(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            next: { tags: [FACILITY_TAG] },
            body: JSON.stringify(impiantiDTO),
            ...options,
        })
        const newFacility = mapToFacility(impiantiDTOResponse)
        return newFacility
    },
    delete: async (id, options) => {
        const url = new URL(`${FACILITY_URL}/${id.toString()}`)
        await makeRequest(url, {
            method: 'DELETE',
            next: { tags: [FACILITY_TAG] },
            ...options,
        })
    },
    getManyReports: async (itemsPerPage, params, options) => {
        const url = new URL(`${FACILITY_URL}/Report/Pagination`)
        url.searchParams.append('PageSize', itemsPerPage ?? '999999')
        if (params?.page) url.searchParams.append('PageIndex', params.page)
        if (params?.order) url.searchParams.append('Descending', params.order === 'desc' ? 'true' : 'false')
        if (params?.sort) url.searchParams.append('SortBy', capitalizeFirstLetter(params.sort))

        if (params?.search) url.searchParams.append('Filters[search]', params.search)

        const reportImpiantoPaginationDTO = await makeRequest<ReportImpiantoPaginationDTO>(url, {
            method: 'GET',
            next: { tags: [FACILITY_TAG] },
            ...options,
        })
        const facilityReports = mapToFacilityReports(reportImpiantoPaginationDTO)
        return facilityReports
    },
    generateReport: async (type, facilityId, datetimeFrom, datetimeTo, options) => {
        const url = new URL(`${FACILITY_URL}/Report`)
        const reportImpiantoDTO = await makeRequest<ReportImpiantoDTO>(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                tipologia: type,
                idImpianto: facilityId,
                datetimeFrom,
                datetimeTo,
            }),
            next: { tags: [FACILITY_TAG] },
            ...options,
        })
        const report = mapToFacilityReport(reportImpiantoDTO)
        return report
    },
    deleteReport: async (id, options) => {
        const url = new URL(`${FACILITY_URL}/Report/${id.toString()}`)
        await makeRequest(url, {
            method: 'DELETE',
            next: { tags: [FACILITY_TAG] },
            ...options,
        })
    },
    putIfcMeta: async (facilityId, ifcmeta, options) => {
        const url = new URL(`${FACILITY_URL}/${facilityId.toString()}/IfcMeta`)
        await makeRequest(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(ifcmeta),
            ...options,
        })
    }
})

export { FACILITY_TAG, facilityCRUD, type FacilityCrudType, type FacilityParams as FacilityGetOptions }
